import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import api from '../../api/axios';
import FilterSelect from '../../components/FilterSelect';
import {
  AdminFilterSelect,
  AdminPageHeader,
  AdminStatusBadge,
  AdminTableCard,
  useAdminList,
} from './AdminUi';
import './admin.css';

const STATUS_OPTIONS = ['PENDING', 'ACCEPTED', 'REJECTED', 'CANCELLED', 'COMPLETED', 'MISSED'];

const STATUS_SELECT_OPTIONS = STATUS_OPTIONS.map((s) => ({ value: s, label: s }));

const FILTER_OPTIONS = [
  { value: '', label: 'All statuses' },
  ...STATUS_SELECT_OPTIONS,
];

function buildExcelRows(rows) {
  return rows.map((a) => ({
    ID: a.id,
    Patient: a.user_name || '',
    'Patient Email': a.user_email || '',
    Doctor: a.doctor_name || '',
    Date: a.date || '',
    'Start Time': a.start_time || '',
    'End Time': a.end_time || '',
    Status: a.status || '',
    'Created At': a.created_at
      ? new Date(a.created_at).toLocaleString()
      : '',
  }));
}

export default function AdminAppointments() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState(searchParams.get('status') || '');
  const [exporting, setExporting] = useState(false);
  const { data, loading, error, load } = useAdminList(
    () => api.get('admin/appointments/', { params: { status: status || undefined, limit: 100 } }),
    [status]
  );

  const changeStatus = async (id, newStatus) => {
    try {
      await api.patch(`admin/appointments/${id}/`, { status: newStatus });
      toast.success(`Appointment #${id} → ${newStatus}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Update failed');
    }
  };

  const downloadExcel = async () => {
    setExporting(true);
    try {
      const res = await api.get('admin/appointments/', {
        params: { status: status || undefined, limit: 200 },
      });
      const rows = res.data?.results || [];
      if (rows.length === 0) {
        toast.warning('No bookings to export');
        return;
      }

      const sheet = XLSX.utils.json_to_sheet(buildExcelRows(rows));
      sheet['!cols'] = [
        { wch: 8 },
        { wch: 20 },
        { wch: 28 },
        { wch: 20 },
        { wch: 14 },
        { wch: 12 },
        { wch: 12 },
        { wch: 12 },
        { wch: 22 },
      ];
      const book = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(book, sheet, 'Bookings');

      const stamp = new Date().toISOString().slice(0, 10);
      const suffix = status ? `_${status.toLowerCase()}` : '_all';
      XLSX.writeFile(book, `fitnova_bookings${suffix}_${stamp}.xlsx`);
      toast.success(`Exported ${rows.length} booking(s) to Excel`);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to export Excel');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div>
      <AdminPageHeader title="Appointments" subtitle={`${data.total} total bookings`}>
        <div className="admin-appointments-toolbar">
          <button
            type="button"
            className="btn btn-outline-success rounded-pill px-3 text-nowrap"
            onClick={downloadExcel}
            disabled={exporting || loading}
          >
            {exporting ? (
              <span className="spinner-border spinner-border-sm me-2" />
            ) : (
              <i className="bi bi-file-earmark-excel me-1" />
            )}
            {exporting ? 'Exporting…' : 'Download Excel'}
          </button>
          <AdminFilterSelect
            value={status}
            onChange={setStatus}
            options={FILTER_OPTIONS}
            placeholder="All statuses"
            aria-label="Filter by status"
          />
        </div>
      </AdminPageHeader>

      {error && <div className="alert alert-danger">{error}</div>}
      {loading ? (
        <div className="text-center py-5"><div className="spinner-border text-primary" /></div>
      ) : data.results.length === 0 ? (
        <div className="admin-card p-4 text-center text-muted">No appointments</div>
      ) : (
        <>
          {/* Mobile / narrow screens: stacked cards */}
          <div className="admin-mobile-list d-md-none">
            {data.results.map((a) => (
              <article key={a.id} className="admin-mobile-card">
                <div className="admin-mobile-card-top">
                  <div className="min-w-0">
                    <div className="admin-mobile-card-id">#{a.id}</div>
                    <div className="admin-mobile-card-title text-truncate">{a.user_name || 'Patient'}</div>
                    <div className="admin-mobile-card-sub text-truncate">{a.user_email || '—'}</div>
                  </div>
                  <AdminStatusBadge status={a.status} />
                </div>

                <div className="admin-mobile-card-meta">
                  <div>
                    <span className="label">Doctor</span>
                    <span className="value">{a.doctor_name || '—'}</span>
                  </div>
                  <div>
                    <span className="label">Date</span>
                    <span className="value">{a.date || '—'}</span>
                  </div>
                  <div>
                    <span className="label">Time</span>
                    <span className="value">
                      {a.start_time || '—'}
                      {a.end_time ? ` – ${a.end_time}` : ''}
                    </span>
                  </div>
                </div>

                <div className="admin-mobile-card-actions">
                  <label className="small text-muted mb-1 d-block">Change status</label>
                  <FilterSelect
                    value={a.status}
                    onChange={(val) => changeStatus(a.id, val)}
                    options={STATUS_SELECT_OPTIONS}
                    size="sm"
                    variant="bordered"
                    aria-label="Change status"
                  />
                </div>
              </article>
            ))}
          </div>

          {/* Tablet+ : table */}
          <div className="d-none d-md-block">
            <AdminTableCard>
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Patient</th>
                    <th>Doctor</th>
                    <th>Date / Time</th>
                    <th>Status</th>
                    <th>Change</th>
                  </tr>
                </thead>
                <tbody>
                  {data.results.map((a) => (
                    <tr key={a.id}>
                      <td>{a.id}</td>
                      <td>
                        <div className="fw-semibold">{a.user_name}</div>
                        <div className="small text-muted text-break">{a.user_email}</div>
                      </td>
                      <td>{a.doctor_name}</td>
                      <td className="small">
                        {a.date || '—'}
                        <br />
                        {a.start_time || ''}{a.end_time ? ` – ${a.end_time}` : ''}
                      </td>
                      <td><AdminStatusBadge status={a.status} /></td>
                      <td style={{ minWidth: 140 }}>
                        <FilterSelect
                          value={a.status}
                          onChange={(val) => changeStatus(a.id, val)}
                          options={STATUS_SELECT_OPTIONS}
                          size="sm"
                          variant="bordered"
                          menuAlign="end"
                          aria-label="Change status"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </AdminTableCard>
          </div>
        </>
      )}
    </div>
  );
}
